import { useCallback } from 'react';
import { switchcase, useNoticeBannersStackContext } from '@sg/garnish';

import { type PaymentMethod } from '@order/graphql';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import {
  useSubmitBraintreeBillingAccountMutation,
  useSubmitPaymentMethodMutation,
} from './graphql/SubmitPaymentMethod.generated';

/**
 * Depending on the feature flag, this will either:
 * - Submit a Billing Account through monograph-svc.
 * - Submit both Billing Accounts and Payment Method Card through payments-svc.
 */
export function useSubmitPaymentMethod(props: SubmitPaymentMethodProps) {
  const { onSuccess } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { track } = useTelemetry();
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { parseBillingAccountError } = useBillingAccountError();
  const shouldUseStripe = useFeatureFlag(
    'CELS-1108-use-stripe-for-new-payment-methods-enabled',
  );

  // ─── Billing Account Submission ──────────────────────────────────────

  const [submitBillingAccountResponse, submitBillingAccountMutation] =
    useSubmitBraintreeBillingAccountMutation();

  const submitBillingAccount = useCallback(
    async (payload: SubmitPaymentMethodPayload) => {
      const response = await submitBillingAccountMutation({
        input: castPayloadToBillingAccount(payload),
      });

      const result = response.data?.submitBraintreeBillingAccount;
      const isSuccess =
        result?.__typename === 'SubmitBraintreeBillingAccountSuccessResponse';
      const userError = parseBillingAccountError(response);

      if (isSuccess) {
        const paymentMethod = result.billingAccount;

        return { isSuccess, userError, paymentMethod };
      }

      return { isSuccess, userError, paymentMethod: undefined };
    },
    [submitBillingAccountMutation, parseBillingAccountError],
  );

  // ─── Payment Method Card Submission ──────────────────────────────────

  const [submitPaymentMethodCardResponse, submitPaymentMethodCardMutation] =
    useSubmitPaymentMethodMutation();

  const submitPaymentMethodCard = useCallback(
    async (payload: SubmitPaymentMethodPayload) => {
      const response = await submitPaymentMethodCardMutation({
        input: payload,
      });

      const paymentMethod = response.data?.updatePaymentMethod;
      const isSuccess = paymentMethod?.__typename === 'PaymentMethodCard';
      const userError = t('credit-card-form.error.save');

      if (isSuccess) {
        return { isSuccess, userError, paymentMethod };
      }

      return { isSuccess, userError, paymentMethod: undefined };
    },
    [submitPaymentMethodCardMutation, t],
  );

  // ─── Hybrid Payment Method Submission ────────────────────────────────

  const submitPaymentMethod = useCallback(
    async (payload: SubmitPaymentMethodPayload) => {
      const { isSuccess, userError, paymentMethod } = shouldUseStripe
        ? await submitPaymentMethodCard(payload)
        : await submitBillingAccount(payload);

      if (isSuccess) {
        track('payment_credit.save_card');
        addNoticeBanner({
          text: t('credit-card-form.save.success'),
          palette: 'success',
        });

        onSuccess(paymentMethod);

        return;
      }

      track('payment_credit.save_card_failed', { userError });
      addNoticeBanner({ text: userError, palette: 'caution' });
    },
    [
      shouldUseStripe,
      t,
      track,
      addNoticeBanner,
      submitPaymentMethodCard,
      submitBillingAccount,
      onSuccess,
    ],
  );

  return {
    submitPaymentMethod,
    isSubmitting: shouldUseStripe
      ? submitPaymentMethodCardResponse.fetching
      : submitBillingAccountResponse.fetching,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type SubmitPaymentMethodProps = Readonly<{
  onSuccess: (paymentMethod: PaymentMethod) => void;
}>;

type SubmitPaymentMethodPayload = Readonly<{
  id: string;
  nickname: string;
  isDefault: boolean;
}>;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function castPayloadToBillingAccount(payload: SubmitPaymentMethodPayload) {
  return {
    nonce: payload.id,
    nickname: payload.nickname,
    isDefault: payload.isDefault,
  };
}

/**
 * Returns a localized error for known user errors.
 * If the typename is unknown, returns the error message.
 * If there is no error message, returns the default submit error.
 */
function useBillingAccountError() {
  const { t } = useLocalizationContext();

  const parseBillingAccountError = useCallback(
    (
      response: Partial<
        ReturnType<typeof useSubmitBraintreeBillingAccountMutation>[0]
      >,
    ) => {
      const errorMessage =
        getResponseErrorMessage(response) ?? t('credit-card-form.error.save');

      return switchcase({
        AddCardFunctionalityDeprecated: t('credit-card-form.error.deprecated'),
        InvalidSubmittedBillingAccount: t(
          'credit-card-form.error.billing.invalid',
        ),
        InvalidPostalCode: t('credit-card-form.error.zipCode.invalid'),
        TooManyRequests: t('credit-card-form.error.too-many-requests'),
      })(errorMessage)(
        response.data?.submitBraintreeBillingAccount?.__typename ?? '',
      );
    },
    [t],
  );

  return { parseBillingAccountError };
}

function getResponseErrorMessage(
  response: Partial<
    ReturnType<typeof useSubmitBraintreeBillingAccountMutation>[0]
  >,
) {
  if (
    response.data?.submitBraintreeBillingAccount.__typename !==
    'SubmitBraintreeBillingAccountSuccessResponse'
  ) {
    return (
      response.data?.submitBraintreeBillingAccount?.errorMessage ??
      response.error?.message
    );
  }
}
